import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useNavigate } from 'react-router-dom';

const ServiceHero = () => {
  const [currentService, setCurrentService] = useState(0);
  const navigate = useNavigate();

  const services = [
    {
      id: 1,
      title: "Custom Software Development",
      subtitle: "Tailored Solutions for Your Business",
      description: "Transform your unique business requirements into powerful, scalable software solutions that drive growth and efficiency.",
      icon: "Code2",
      gradient: "from-accent/20 to-blue-500/20",
      stats: { projects: "150+", satisfaction: "98%" }
    },
    {
      id: 2,
      title: "Web Application Engineering",
      subtitle: "Modern Web Experiences",
      description: "Build responsive, high-performance web applications using cutting-edge technologies and best practices.",
      icon: "Globe",
      gradient: "from-success/20 to-emerald-500/20",
      stats: { projects: "200+", satisfaction: "99%" }
    },
    {
      id: 3,
      title: "Mobile Solutions",
      subtitle: "Native & Cross-Platform Apps",
      description: "Create engaging mobile experiences that work seamlessly across iOS and Android platforms.",
      icon: "Smartphone",
      gradient: "from-warning/20 to-orange-500/20",
      stats: { projects: "120+", satisfaction: "97%" }
    },
    {
      id: 4,
      title: "UI/UX Design",
      subtitle: "User-Centered Design Excellence",
      description: "Design intuitive interfaces that delight users and drive conversions through thoughtful user experience.",
      icon: "Palette",
      gradient: "from-purple-500/20 to-pink-500/20",
      stats: { projects: "180+", satisfaction: "99%" }
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentService((prev) => (prev + 1) % services?.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [services?.length]);

  const currentServiceData = services?.[currentService];

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-background via-primary to-background overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-32 h-32 bg-accent/10 rounded-full blur-xl floating-animation"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-success/10 rounded-full blur-lg floating-animation" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-40 left-1/4 w-40 h-40 bg-warning/10 rounded-full blur-2xl floating-animation" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-20 right-1/3 w-28 h-28 bg-purple-500/10 rounded-full blur-xl floating-animation" style={{ animationDelay: '3s' }}></div>
      </div>
      <div className="relative z-10 container mx-auto px-6 lg:px-8 pt-24 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium"
              >
                <Icon name="Sparkles" size={16} />
                <span>Services Laboratory</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-4xl lg:text-6xl font-bold text-foreground leading-tight"
              >
                Transform Ideas Into
                <span className="block bg-gradient-to-r from-accent to-success bg-clip-text text-transparent">
                  Digital Reality
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-xl text-muted-foreground leading-relaxed"
              >
                We lift the shutter on digital possibilities through cutting-edge software development, 
                innovative design, and strategic technology solutions that drive business growth.
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button
                variant="default"
                size="lg"
                iconName="Rocket"
                iconPosition="left"
                //routing to contact
                onClick = {() => navigate("/contact")}
                className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-accent"
              >
                Start Your Project
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Play"
                iconPosition="left"
                className="border-accent/30 text-accent "
              >
                Watch Demo
              </Button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="grid grid-cols-3 gap-8 pt-8 border-t border-border"
            >
              <div className="text-center">
                <div className="text-2xl font-bold text-accent">40+</div>
                <div className="text-sm text-muted-foreground">Projects Delivered</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-success">95%</div>
                <div className="text-sm text-muted-foreground">Client Satisfaction</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-warning">1+</div>
                <div className="text-sm text-muted-foreground">Years Experience</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Service Showcase */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative perspective-1000">
              <motion.div
                key={currentService}
                initial={{ rotateY: 90, opacity: 0 }}
                animate={{ rotateY: 0, opacity: 1 }}
                exit={{ rotateY: -90, opacity: 0 }}
                transition={{ duration: 0.6 }}
                className={`relative bg-gradient-to-br ${currentServiceData?.gradient} backdrop-blur-sm border border-border/50 rounded-2xl p-8 shadow-3d transform-3d`}
              >
                {/* Service Icon */}
                <div className="flex items-center justify-center w-16 h-16 bg-accent/20 rounded-xl mb-6 mx-auto">
                  <Icon name={currentServiceData?.icon} size={32} className="text-accent" />
                </div>

                {/* Service Content */}
                <div className="text-center space-y-4">
                  <h3 className="text-2xl font-bold text-foreground">
                    {currentServiceData?.title}
                  </h3>
                  <p className="text-accent font-medium">
                    {currentServiceData?.subtitle}
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    {currentServiceData?.description}
                  </p>

                  {/* Service Stats */}
                  <div className="flex justify-center space-x-8 pt-4">
                    <div className="text-center">
                      <div className="text-lg font-bold text-accent">
                        {currentServiceData?.stats?.projects}
                      </div>
                      <div className="text-xs text-muted-foreground">Projects</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-success">
                        {currentServiceData?.stats?.satisfaction}
                      </div>
                      <div className="text-xs text-muted-foreground">Satisfaction</div>
                    </div>
                  </div>
                </div>

                {/* Floating Elements */}
                <div className="absolute -top-4 -right-4 w-8 h-8 bg-accent/20 rounded-full blur-sm floating-animation"></div>
                <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-success/20 rounded-full blur-sm floating-animation" style={{ animationDelay: '1s' }}></div>
              </motion.div>
            </div>

            {/* Service Navigation Dots */}
            <div className="flex justify-center space-x-3 mt-8">
              {services?.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentService(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentService
                      ? 'bg-accent shadow-accent scale-125'
                      : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                  }`}
                  aria-label={`Switch to service ${index + 1}`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </div>
      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <div className="flex flex-col items-center space-y-2 text-muted-foreground">
          <span className="text-sm">Explore Services</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Icon name="ChevronDown" size={20} />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default ServiceHero;